/**
 * 图片懒加载和WebP支持模块
 * 实现图片延迟加载和格式优化
 */

class LazyImageLoader {
  constructor(options = {}) {
    this.options = {
      rootMargin: '50px',
      threshold: 0.1,
      enableWebP: true,
      enableRetina: true,
      placeholderColor: '#f0f0f0',
      fadeInDuration: 300,
      ...options
    };

    this.observer = null;
    this.supportsWebP = false;
    this.supportsAvif = false;
    this.devicePixelRatio = window.devicePixelRatio || 1;

    this.init();
  }

  /**
     * 初始化懒加载系统
     */
  async init() {
    // 检测WebP和AVIF支持
    await this.detectImageFormats();

    // 创建Intersection Observer
    this.createObserver();

    // 扫描并处理现有图片
    this.processExistingImages();

    // 监听DOM变化，处理动态添加的图片
    this.observeNewImages();

    console.log('[LazyLoad] Image lazy loading initialized', {
      webp: this.supportsWebP,
      avif: this.supportsAvif,
      dpr: this.devicePixelRatio
    });
  }

  /**
     * 检测浏览器对现代图片格式的支持
     */
  async detectImageFormats() {
    const formatTests = {
      webp: 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA',
      avif: 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgABogQEAwgMg8f8D///8WfhwB8+ErK42A='
    };

    const promises = Object.entries(formatTests).map(([format, dataUrl]) =>
      this.canLoadImage(dataUrl).then(supported => [format, supported])
    );

    const results = await Promise.all(promises);

    results.forEach(([format, supported]) => {
      if (format === 'webp') {this.supportsWebP = supported;}
      if (format === 'avif') {this.supportsAvif = supported;}
    });
  }

  /**
     * 测试图片格式支持
     */
  canLoadImage(src) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  }

  /**
     * 创建Intersection Observer
     */
  createObserver() {
    if (!('IntersectionObserver' in window)) {
      console.warn('[LazyLoad] IntersectionObserver not supported, falling back to immediate loading');
      this.loadAllImages();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold
    });
  }

  /**
     * 处理现有的图片元素
     */
  processExistingImages() {
    const images = document.querySelectorAll('img[data-src], img[data-srcset], [data-bg-src]');
    images.forEach(img => this.setupLazyImage(img));
  }

  /**
     * 观察新添加的图片
     */
  observeNewImages() {
    if ('MutationObserver' in window) {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // 检查添加的节点本身
              if (this.isLazyImage(node)) {
                this.setupLazyImage(node);
              }
              // 检查子节点
              const lazyImages = node.querySelectorAll &&
                                node.querySelectorAll('img[data-src], img[data-srcset], [data-bg-src]');
              if (lazyImages) {
                lazyImages.forEach(img => this.setupLazyImage(img));
              }
            }
          });
        });
      });

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
     * 判断是否为懒加载图片
     */
  isLazyImage(element) {
    return element.hasAttribute('data-src') ||
               element.hasAttribute('data-srcset') ||
               element.hasAttribute('data-bg-src');
  }

  /**
     * 设置懒加载图片
     */
  setupLazyImage(img) {
    // 添加占位符样式
    this.addPlaceholder(img);

    // 开始观察
    if (this.observer) {
      this.observer.observe(img);
    } else {
      // 如果不支持IntersectionObserver，直接加载
      this.loadImage(img);
    }
  }

  /**
     * 添加占位符
     */
  addPlaceholder(element) {
    if (element.tagName === 'IMG') {
      // 为img元素添加占位符
      if (!element.src) {
        element.src = this.generatePlaceholder(
          element.dataset.width || 300,
          element.dataset.height || 200
        );
      }
    } else {
      // 为背景图片元素添加占位符
      element.style.backgroundColor = this.options.placeholderColor;
    }

    // 添加懒加载类
    element.classList.add('lazy-loading');
  }

  /**
     * 生成占位符图片
     */
  generatePlaceholder(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = this.options.placeholderColor;
    ctx.fillRect(0, 0, width, height);

    return canvas.toDataURL();
  }

  /**
     * 加载图片
     */
  async loadImage(element) {
    try {
      const src = this.getOptimalImageSrc(element);

      if (element.tagName === 'IMG') {
        await this.loadImageElement(element, src);
      } else {
        await this.loadBackgroundImage(element, src);
      }

      this.onImageLoaded(element);
    } catch (error) {
      console.error('[LazyLoad] Failed to load image:', error);
      this.onImageError(element);
    }
  }

  /**
     * 获取最优图片源
     */
  getOptimalImageSrc(element) {
    const dataSrc = element.dataset.src;
    const dataSrcset = element.dataset.srcset;
    const dataBgSrc = element.dataset.bgSrc;

    // 优先使用背景图片源
    if (dataBgSrc) {
      return this.getOptimalFormat(dataBgSrc);
    }

    // 处理srcset
    if (dataSrcset) {
      const srcset = this.parseAndOptimizeSrcset(dataSrcset);
      element.srcset = srcset;
    }

    // 处理普通src
    if (dataSrc) {
      return this.getOptimalFormat(dataSrc);
    }

    return null;
  }

  /**
     * 获取最优图片格式
     */
  getOptimalFormat(originalSrc) {
    if (!this.options.enableWebP && !this.supportsAvif) {
      return this.getRetinaImage(originalSrc);
    }

    // 尝试现代格式
    const baseSrc = originalSrc.split('.').slice(0, -1).join('.');

    // 优先AVIF，然后WebP，最后原格式
    if (this.supportsAvif) {
      const avifSrc = `${baseSrc}.avif`;
      return this.getRetinaImage(avifSrc);
    } else if (this.supportsWebP && this.options.enableWebP) {
      const webpSrc = `${baseSrc}.webp`;
      return this.getRetinaImage(webpSrc);
    }

    return this.getRetinaImage(originalSrc);
  }

  /**
     * 获取高分辨率图片
     */
  getRetinaImage(src) {
    if (!this.options.enableRetina || this.devicePixelRatio <= 1) {
      return src;
    }

    // 对于2x及以上设备，尝试获取高分辨率版本
    const parts = src.split('.');
    const extension = parts.pop();
    const baseName = parts.join('.');

    return `${baseName}@2x.${extension}`;
  }

  /**
     * 解析并优化srcset
     */
  parseAndOptimizeSrcset(srcset) {
    return srcset.split(',').map(src => {
      const [url, descriptor] = src.trim().split(' ');
      const optimizedUrl = this.getOptimalFormat(url);
      return descriptor ? `${optimizedUrl} ${descriptor}` : optimizedUrl;
    }).join(', ');
  }

  /**
     * 加载img元素
     */
  loadImageElement(img, src) {
    return new Promise((resolve, reject) => {
      const tempImg = new Image();

      tempImg.onload = () => {
        img.src = src;
        resolve();
      };

      tempImg.onerror = reject;
      tempImg.src = src;
    });
  }

  /**
     * 加载背景图片
     */
  loadBackgroundImage(element, src) {
    return new Promise((resolve, reject) => {
      const tempImg = new Image();

      tempImg.onload = () => {
        element.style.backgroundImage = `url("${src}")`;
        resolve();
      };

      tempImg.onerror = reject;
      tempImg.src = src;
    });
  }

  /**
     * 图片加载成功处理
     */
  onImageLoaded(element) {
    element.classList.remove('lazy-loading');
    element.classList.add('lazy-loaded');

    // 淡入动画
    if (this.options.fadeInDuration > 0) {
      element.style.transition = `opacity ${this.options.fadeInDuration}ms ease-in-out`;
      element.style.opacity = '0';

      // 强制重绘
      element.offsetHeight;

      element.style.opacity = '1';
    }

    // 移除数据属性
    delete element.dataset.src;
    delete element.dataset.srcset;
    delete element.dataset.bgSrc;

    // 触发自定义事件
    element.dispatchEvent(new CustomEvent('lazyImageLoaded', {
      bubbles: true,
      detail: { element }
    }));
  }

  /**
     * 图片加载失败处理
     */
  onImageError(element) {
    element.classList.remove('lazy-loading');
    element.classList.add('lazy-error');

    // 触发自定义事件
    element.dispatchEvent(new CustomEvent('lazyImageError', {
      bubbles: true,
      detail: { element }
    }));
  }

  /**
     * 立即加载所有图片（降级方案）
     */
  loadAllImages() {
    const images = document.querySelectorAll('img[data-src], img[data-srcset], [data-bg-src]');
    images.forEach(img => this.loadImage(img));
  }

  /**
     * 销毁懒加载实例
     */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// 创建全局实例
window.lazyImageLoader = new LazyImageLoader();

// 自动初始化
document.addEventListener('DOMContentLoaded', () => {
  if (!window.lazyImageLoader) {
    window.lazyImageLoader = new LazyImageLoader();
  }
});

// 导出类
window.LazyImageLoader = LazyImageLoader;